/**
 * The public projection contract, parsed strictly and failed closed.
 *
 * This module is the security boundary of the storefront. MASTER_SPEC 9.1 and
 * Design Bible 10.2 forbid a customer surface from ever seeing private original
 * keys, internal notes, consignor identity, storage location, acquisition cost,
 * floor price, minimum net proceeds, or AI confidence. Core's WP04 projections
 * (`PublicCatalogItem`, `CheckoutStatusResult`) already select only public
 * columns, so this file's job is to make that guarantee *structural on this
 * side too*: a component cannot receive a field the parser did not admit.
 *
 * Three rules, in this order:
 *
 * 1. **Exact keys.** A payload carrying a key this contract does not declare is
 *    rejected outright, not ignored. A silently-dropped unknown key is how a
 *    private field ends up in a log line or a React DevTools tree.
 * 2. **Forbidden names, recursively.** Independently of 1, the whole payload is
 *    walked for the private field names listed in `FORBIDDEN_KEYS`. If core
 *    ever regressed and widened a projection, this surface refuses the response
 *    rather than rendering it. Failing closed loses a page; failing open leaks
 *    a consignor.
 * 3. **Bounded values.** Every string has a length ceiling and every integer is
 *    range-checked, so hostile catalog text cannot become an unbounded render.
 *
 * Every string that survives is still *untrusted display text* (Bible 11.5):
 * React escapes it, no component sets `dangerouslySetInnerHTML`, and no value
 * from here is ever interpolated into a URL without the validators below.
 */

export type ContractResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly reason: string };

const MAX_TEXT = 2_000;
const MAX_ID = 100;
const MAX_URL = 2_048;
const MAX_ITEMS = 50;
const MAX_MEDIA = 20;
const MAX_LINES = 20;
/** MASTER_SPEC 0.5.6: money is a signed integer minor unit. Core caps a price well below this. */
const MAX_MINOR = 100_000_000;

/**
 * Private field names that must never reach a customer surface (MASTER_SPEC
 * 9.1). The list is checked against every key at every depth of a response.
 */
const FORBIDDEN_KEYS: ReadonlySet<string> = new Set([
  "private_object_key",
  "object_key",
  "original_object_key",
  "internal_notes",
  "notes_internal",
  "consignor_id",
  "consignor",
  "owner_type",
  "owner_id",
  "box_id",
  "box_code",
  "slot_position",
  "storage_location",
  "location_description",
  "acquisition_cost_minor",
  "cost_minor",
  "floor_price_minor",
  "min_net_proceeds_minor",
  "minimum_net_minor",
  "fee_bps",
  "confidence",
  "confidence_bps",
  "ai_confidence",
  "model",
  "prompt_version",
  "address",
  "shipping_address",
  "shipping_address_encrypted",
  "authorized_shipping",
  "address_hmac",
  "guest_email",
  "guest_email_hash",
  "contact_email_encrypted",
  "recipient_email",
  "token",
  "csrf_token_hash",
  "secret",
  "api_key",
  "bearer"
]);

/** The item states core's public queries can return (MASTER_SPEC 6.1). */
export const publicAvailability = ["ACTIVE_WEBSITE", "ACTIVE_EBAY"] as const;
export type PublicAvailability = (typeof publicAvailability)[number];

export const publicChannels = ["WEBSITE", "EBAY"] as const;
export type PublicChannel = (typeof publicChannels)[number];

/**
 * A single exact object, as the public catalog publishes it.
 *
 * `cert_number` is declared optional on purpose. Core's WP04 public projections
 * select `grading_company` and `grade` but not `cert_number`, even though the
 * column exists on `physical_items`. The Design Bible's item page (12.1) puts
 * the certification number in the Slab Frame's label bar because it is the
 * fact a collector cross-checks before spending four figures. This contract
 * therefore accepts the field if a future projection publishes it and validates
 * it as strictly as everything else — and when it is absent the surface says so
 * in words (see `components/SlabFrame.tsx`) instead of rendering a placeholder.
 */
export interface PublicItem {
  readonly product_id: string;
  readonly physical_item_id: string;
  readonly sku: string;
  readonly title: string;
  readonly platform: string;
  readonly region: string;
  readonly publisher?: string;
  readonly developer?: string;
  readonly edition_variant: string;
  readonly description?: string;
  readonly condition: string;
  readonly grading_company?: string;
  readonly grade?: string;
  readonly cert_number?: string;
  readonly listing_id: string;
  readonly channel: PublicChannel;
  readonly availability: PublicAvailability;
  readonly public_url: string;
  readonly checkout_eligible: boolean;
  readonly price_minor: number;
  readonly currency: "USD";
  readonly public_derivatives: readonly string[];
}

const ITEM_KEYS: readonly string[] = [
  "product_id",
  "physical_item_id",
  "sku",
  "title",
  "platform",
  "region",
  "publisher",
  "developer",
  "edition_variant",
  "description",
  "condition",
  "grading_company",
  "grade",
  "cert_number",
  "listing_id",
  "channel",
  "availability",
  "public_url",
  "checkout_eligible",
  "price_minor",
  "currency",
  "public_derivatives"
];

export interface CheckoutLine {
  readonly physical_item_id: string;
  readonly sku: string;
  /** Immutable provider line name; does not snapshot grade, cert or media. */
  readonly display_name?: string;
  readonly base_price_minor: number;
  readonly discount_minor: number;
  readonly tax_minor: number;
  readonly final_line_total_minor: number;
}

const LINE_KEYS: readonly string[] = [
  "physical_item_id",
  "sku",
  "display_name",
  "base_price_minor",
  "discount_minor",
  "tax_minor",
  "final_line_total_minor"
];

/** Quote statuses core writes, then order statuses once a quote becomes an order. */
export const checkoutStatuses = [
  "TAX_PENDING",
  "READY",
  "CONSUMED",
  "EXPIRED",
  "CANCELLED",
  "PENDING_PAYMENT",
  "PAID"
] as const;
export type CheckoutStatus = (typeof checkoutStatuses)[number];

/**
 * `orders.fulfillment_status` and `orders.hold_reason`.
 *
 * Both are optional for the same reason `cert_number` is: core writes them on
 * the order record — `ON_HOLD` with `SELLER_PROTECTION_INELIGIBLE` or
 * `HIGH_VALUE_REVIEW` after an authoritative capture, and `ON_HOLD` with
 * `PROVIDER_ADDRESS_MISMATCH` while the order is still `PENDING_PAYMENT` and
 * PayPal's authorised address did not match the one the customer entered. The
 * owner-bound WP04 status projection carries them when present. When they are
 * absent the confirmation refuses to make any claim about shipment readiness
 * at all (see `features/ConfirmationView.tsx`); when they
 * are present it states the hold and its recovery. Neither branch invents a
 * fulfilment state, which is the rule that matters.
 */
export const fulfillmentStatuses = ["UNFULFILLED", "ON_HOLD"] as const;
export type FulfillmentStatus = (typeof fulfillmentStatuses)[number];

export const holdReasons = [
  "SELLER_PROTECTION_INELIGIBLE",
  "HIGH_VALUE_REVIEW",
  "PROVIDER_ADDRESS_MISMATCH",
  "CHECKOUT_RECONCILIATION_REQUIRED"
] as const;
export type HoldReason = (typeof holdReasons)[number];

export interface CheckoutState {
  readonly schema_version: string;
  readonly quote_id: string;
  readonly order_id?: string;
  readonly status: CheckoutStatus;
  readonly currency: "USD";
  readonly item_subtotal_minor: number;
  readonly discount_minor: number;
  readonly shipping_minor: number;
  readonly insurance_minor: number;
  readonly tax_minor: number;
  readonly total_minor: number;
  readonly expires_at: string;
  readonly lines: readonly CheckoutLine[];
  /** Only ever set from a server response; the browser never synthesises one. */
  readonly approval_url?: string;
  readonly fulfillment_status?: FulfillmentStatus;
  readonly hold_reason?: HoldReason;
}

const STATE_KEYS: readonly string[] = [
  "schema_version",
  "quote_id",
  "order_id",
  "status",
  "currency",
  "item_subtotal_minor",
  "discount_minor",
  "shipping_minor",
  "insurance_minor",
  "tax_minor",
  "total_minor",
  "expires_at",
  "lines",
  "approval_url",
  "fulfillment_status",
  "hold_reason"
];

/** `CheckoutStartResult`: a started checkout, before tax has resolved. */
export interface CheckoutStarted extends CheckoutState {
  readonly tax_job_id: string;
  /** Guest continuation capability. Opaque; never rendered, never logged. */
  readonly checkout_token?: string;
}

export interface InquiryReceipt {
  readonly inquiry_id: string;
  readonly status: string;
  readonly replayed?: boolean;
}

export interface CustomerSessionReceipt {
  readonly csrf_token: string;
  readonly expires_at: string;
}

/* Primitive guards ---------------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Rule 2. Walks the entire payload and refuses it if any key at any depth is a
 * private field name. Depth and breadth are bounded so a hostile response
 * cannot turn validation itself into the denial of service.
 */
function carriesForbiddenKey(value: unknown, depth = 0): string | null {
  if (depth > 8) return "response nests deeper than the public contract allows";
  if (Array.isArray(value)) {
    if (value.length > 200) return "response array exceeds the public contract bound";
    for (const entry of value) {
      const found = carriesForbiddenKey(entry, depth + 1);
      if (found !== null) return found;
    }
    return null;
  }
  if (!isRecord(value)) return null;
  for (const [key, nested] of Object.entries(value)) {
    if (FORBIDDEN_KEYS.has(key)) return `response carried the private field "${key}"`;
    const found = carriesForbiddenKey(nested, depth + 1);
    if (found !== null) return found;
  }
  return null;
}

function exactKeys(value: Record<string, unknown>, allowed: readonly string[]): string | null {
  const permitted = new Set(allowed);
  for (const key of Object.keys(value)) {
    if (!permitted.has(key)) return `unexpected field "${key}"`;
  }
  return null;
}

function text(value: unknown, maximum: number): string | null {
  if (typeof value !== "string" || value.length < 1 || value.length > maximum) return null;
  // C0/C1 controls would let hostile catalog text smuggle line breaks into a
  // label or a log line. Tab, newline, and carriage return are permitted only
  // inside long-form copy, which passes `multiline`.
  if (/[\u0000-\u001f\u007f]/.test(value)) return null;
  return value;
}

function multiline(value: unknown, maximum: number): string | null {
  if (typeof value !== "string" || value.length < 1 || value.length > maximum) return null;
  if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value)) return null;
  return value;
}

function optionalText(
  value: unknown,
  maximum: number
): { readonly ok: true; readonly value: string | undefined } | { readonly ok: false } {
  if (value === undefined) return { ok: true, value: undefined };
  const parsed = text(value, maximum);
  return parsed === null ? { ok: false } : { ok: true, value: parsed };
}

function minor(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) return null;
  if (value < 0 || value > MAX_MINOR) return null;
  return value;
}

/**
 * Approved public media. Core stores a `stable_public_url`; anything that is
 * not an absolute `https` URL or a same-origin absolute path is refused rather
 * than handed to an `img` tag, so a poisoned row cannot become a `javascript:`
 * or `data:` source.
 */
export function isPublicMediaUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length < 1 || value.length > MAX_URL) return false;
  if (value.startsWith("/") && !value.startsWith("//")) return true;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * MASTER_SPEC 9.3: an eBay-owned item links out to eBay and nowhere else. The
 * host allow-list is checked here rather than at the render site so there is
 * exactly one place a `Buy on eBay` destination can come from.
 */
export function isEbayUrl(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:") return false;
    const host = url.hostname.toLowerCase();
    return host === "ebay.com" || host.endsWith(".ebay.com");
  } catch {
    return false;
  }
}

/** The PayPal approval handoff may only target PayPal (MASTER_SPEC 9.6). */
export function isPayPalApprovalUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    const keys = [...url.searchParams.keys()];
    const token = url.searchParams.get("token");
    return (
      url.protocol === "https:" &&
      (host === "www.paypal.com" || host === "www.sandbox.paypal.com") &&
      url.username === "" &&
      url.password === "" &&
      url.port === "" &&
      url.pathname === "/checkoutnow" &&
      url.hash === "" &&
      keys.length === 1 &&
      keys[0] === "token" &&
      token !== null &&
      /^[A-Za-z0-9._~-]{1,200}$/.test(token)
    );
  } catch {
    return false;
  }
}

/* Parsers ------------------------------------------------------------------- */

export function parsePublicItem(raw: unknown): ContractResult<PublicItem> {
  const forbidden = carriesForbiddenKey(raw);
  if (forbidden !== null) return { ok: false, reason: forbidden };
  if (!isRecord(raw)) return { ok: false, reason: "item is not an object" };
  const unexpected = exactKeys(raw, ITEM_KEYS);
  if (unexpected !== null) return { ok: false, reason: unexpected };

  const productID = text(raw["product_id"], MAX_ID);
  const itemID = text(raw["physical_item_id"], MAX_ID);
  const listingID = text(raw["listing_id"], MAX_ID);
  const sku = text(raw["sku"], 100);
  const title = text(raw["title"], 300);
  const platform = text(raw["platform"], 80);
  const region = text(raw["region"], 40);
  const edition = text(raw["edition_variant"], 200);
  const condition = multiline(raw["condition"], MAX_TEXT);
  if (
    productID === null ||
    itemID === null ||
    listingID === null ||
    sku === null ||
    title === null ||
    platform === null ||
    region === null ||
    edition === null
  ) {
    return { ok: false, reason: "item is missing a required public identifier or title" };
  }
  if (condition === null) {
    // MASTER_SPEC 9.1 requires the approved condition statement on every public
    // item. An item without one is not publishable, so it is not rendered.
    return { ok: false, reason: "item has no approved condition statement" };
  }

  const publisher = optionalText(raw["publisher"], 200);
  const developer = optionalText(raw["developer"], 200);
  const gradingCompany = optionalText(raw["grading_company"], 80);
  const grade = optionalText(raw["grade"], 40);
  const certNumber = optionalText(raw["cert_number"], 60);
  if (!publisher.ok || !developer.ok || !gradingCompany.ok || !grade.ok || !certNumber.ok) {
    return { ok: false, reason: "item carried an out-of-bounds descriptive field" };
  }
  let description: string | undefined;
  if (raw["description"] !== undefined) {
    const parsed = multiline(raw["description"], MAX_TEXT);
    if (parsed === null) return { ok: false, reason: "item description is out of bounds" };
    description = parsed;
  }

  const channel = raw["channel"];
  const availability = raw["availability"];
  if (typeof channel !== "string" || !(publicChannels as readonly string[]).includes(channel)) {
    return { ok: false, reason: "item channel is not a public channel" };
  }
  if (
    typeof availability !== "string" ||
    !(publicAvailability as readonly string[]).includes(availability)
  ) {
    return { ok: false, reason: "item availability is not publishable" };
  }
  if (typeof raw["checkout_eligible"] !== "boolean") {
    return { ok: false, reason: "checkout eligibility is not a boolean" };
  }
  const price = minor(raw["price_minor"]);
  if (price === null || price <= 0) return { ok: false, reason: "item price is not a positive integer minor amount" };
  if (raw["currency"] !== "USD") return { ok: false, reason: "item currency is not USD" };

  const publicURL = text(raw["public_url"], MAX_URL);
  if (publicURL === null) return { ok: false, reason: "item has no public URL" };

  /*
   * The two channel invariants, enforced here so no view can contradict them:
   * a WEBSITE listing is the only thing that may ever be checkout-eligible, and
   * an EBAY listing must carry a real eBay destination or it is not renderable
   * as an eBay item at all.
   */
  if (channel === "EBAY" && raw["checkout_eligible"] === true) {
    return { ok: false, reason: "an eBay-owned item cannot be checkout eligible" };
  }
  if (availability === "ACTIVE_EBAY" && !isEbayUrl(publicURL)) {
    return { ok: false, reason: "eBay item does not carry an eBay destination" };
  }
  if (availability === "ACTIVE_WEBSITE" && channel !== "WEBSITE") {
    return { ok: false, reason: "website item is not on the website channel" };
  }

  const derivatives = raw["public_derivatives"];
  if (!Array.isArray(derivatives) || derivatives.length > MAX_MEDIA) {
    return { ok: false, reason: "public media list is missing or over bound" };
  }
  const media = derivatives.filter(isPublicMediaUrl);
  if (media.length !== derivatives.length) {
    return { ok: false, reason: "public media list carried a URL this surface will not load" };
  }

  return {
    ok: true,
    value: {
      product_id: productID,
      physical_item_id: itemID,
      sku,
      title,
      platform,
      region,
      ...(publisher.value === undefined ? {} : { publisher: publisher.value }),
      ...(developer.value === undefined ? {} : { developer: developer.value }),
      edition_variant: edition,
      ...(description === undefined ? {} : { description }),
      condition,
      ...(gradingCompany.value === undefined ? {} : { grading_company: gradingCompany.value }),
      ...(grade.value === undefined ? {} : { grade: grade.value }),
      ...(certNumber.value === undefined ? {} : { cert_number: certNumber.value }),
      listing_id: listingID,
      channel: channel as PublicChannel,
      availability: availability as PublicAvailability,
      public_url: publicURL,
      checkout_eligible: raw["checkout_eligible"],
      price_minor: price,
      currency: "USD",
      public_derivatives: media
    }
  };
}

/** `{ "items": [...] }` — the catalog and Product routes share this envelope. */
export function parseItemList(raw: unknown): ContractResult<readonly PublicItem[]> {
  const forbidden = carriesForbiddenKey(raw);
  if (forbidden !== null) return { ok: false, reason: forbidden };
  if (!isRecord(raw)) return { ok: false, reason: "response is not an object" };
  const unexpected = exactKeys(raw, ["items"]);
  if (unexpected !== null) return { ok: false, reason: unexpected };
  const items = raw["items"];
  if (!Array.isArray(items)) return { ok: false, reason: "response has no items array" };
  if (items.length > MAX_ITEMS) return { ok: false, reason: "response exceeds the page bound" };
  const parsed: PublicItem[] = [];
  for (const entry of items) {
    const item = parsePublicItem(entry);
    // One bad row fails the page. A partly-understood catalog is worse than a
    // stated failure, and it is the shape a leak would arrive in.
    if (!item.ok) return item;
    parsed.push(item.value);
  }
  return { ok: true, value: parsed };
}

function parseCheckoutCommon(
  raw: Record<string, unknown>
): ContractResult<CheckoutState> {
  const quoteID = text(raw["quote_id"], MAX_ID);
  if (quoteID === null) return { ok: false, reason: "checkout has no quote id" };
  const status = raw["status"];
  if (typeof status !== "string" || !(checkoutStatuses as readonly string[]).includes(status)) {
    return { ok: false, reason: "checkout status is not one this surface understands" };
  }
  if (raw["currency"] !== "USD") return { ok: false, reason: "checkout currency is not USD" };
  const schema = text(raw["schema_version"], 40);
  if (schema === null) return { ok: false, reason: "checkout has no schema version" };

  const amounts: Record<string, number> = {};
  for (const name of [
    "item_subtotal_minor",
    "discount_minor",
    "shipping_minor",
    "insurance_minor",
    "tax_minor",
    "total_minor"
  ]) {
    const parsed = minor(raw[name]);
    if (parsed === null) return { ok: false, reason: `checkout ${name} is not an integer minor amount` };
    amounts[name] = parsed;
  }

  const expires = text(raw["expires_at"], 60);
  if (expires === null) return { ok: false, reason: "checkout has no expiry" };

  const rawLines = raw["lines"];
  if (!Array.isArray(rawLines) || rawLines.length > MAX_LINES) {
    return { ok: false, reason: "checkout lines are missing or over bound" };
  }
  const lines: CheckoutLine[] = [];
  for (const entry of rawLines) {
    if (!isRecord(entry)) return { ok: false, reason: "checkout line is not an object" };
    const unexpected = exactKeys(entry, LINE_KEYS);
    if (unexpected !== null) return { ok: false, reason: unexpected };
    const itemID = text(entry["physical_item_id"], MAX_ID);
    const sku = text(entry["sku"], 100);
    const displayName = entry["display_name"];
    // commerce.go validPayPalBreakdown -> intake.go validBoundedText:
    // nonempty, trimmed, no Unicode controls, at most 300 UTF-8 bytes.
    if (displayName !== undefined && (
      typeof displayName !== "string" || displayName.length === 0 ||
      displayName !== displayName.trim() || /\p{Cc}/u.test(displayName) ||
      new TextEncoder().encode(displayName).length > 300
    )) return { ok: false, reason: "checkout display name is out of bounds" };
    const base = minor(entry["base_price_minor"]);
    const discount = minor(entry["discount_minor"]);
    const tax = minor(entry["tax_minor"]);
    const total = minor(entry["final_line_total_minor"]);
    if (itemID === null || sku === null || base === null || discount === null || tax === null || total === null) {
      return { ok: false, reason: "checkout line is not a complete integer money line" };
    }
    lines.push({
      physical_item_id: itemID,
      sku,
      ...(displayName === undefined ? {} : { display_name: displayName }),
      base_price_minor: base,
      discount_minor: discount,
      tax_minor: tax,
      final_line_total_minor: total
    });
  }

  const orderID = optionalText(raw["order_id"], MAX_ID);
  if (!orderID.ok) return { ok: false, reason: "checkout order id is out of bounds" };

  let approval: string | undefined;
  if (raw["approval_url"] !== undefined) {
    const parsed = text(raw["approval_url"], MAX_URL);
    if (parsed === null || !isPayPalApprovalUrl(parsed)) {
      return { ok: false, reason: "checkout approval target is not a PayPal URL" };
    }
    approval = parsed;
  }
  let fulfillment: FulfillmentStatus | undefined;
  if (raw["fulfillment_status"] !== undefined) {
    const value = raw["fulfillment_status"];
    if (typeof value !== "string" || !(fulfillmentStatuses as readonly string[]).includes(value)) {
      return { ok: false, reason: "fulfilment status is not one this surface understands" };
    }
    fulfillment = value as FulfillmentStatus;
  }
  let hold: HoldReason | undefined;
  if (raw["hold_reason"] !== undefined) {
    const value = raw["hold_reason"];
    if (typeof value !== "string" || !(holdReasons as readonly string[]).includes(value)) {
      return { ok: false, reason: "hold reason is not one this surface understands" };
    }
    hold = value as HoldReason;
  }
  if (approval !== undefined && (status !== "PENDING_PAYMENT" || fulfillment === "ON_HOLD" || hold !== undefined)) {
    return { ok: false, reason: "checkout approval target is not in a pending approval state" };
  }
  if (hold !== undefined && fulfillment !== "ON_HOLD") {
    return { ok: false, reason: "checkout hold reason has no authoritative fulfilment hold" };
  }

  return {
    ok: true,
    value: {
      schema_version: schema,
      quote_id: quoteID,
      ...(orderID.value === undefined ? {} : { order_id: orderID.value }),
      status: status as CheckoutStatus,
      currency: "USD",
      item_subtotal_minor: amounts["item_subtotal_minor"] ?? 0,
      discount_minor: amounts["discount_minor"] ?? 0,
      shipping_minor: amounts["shipping_minor"] ?? 0,
      insurance_minor: amounts["insurance_minor"] ?? 0,
      tax_minor: amounts["tax_minor"] ?? 0,
      total_minor: amounts["total_minor"] ?? 0,
      expires_at: expires,
      lines,
      ...(approval === undefined ? {} : { approval_url: approval }),
      ...(fulfillment === undefined ? {} : { fulfillment_status: fulfillment }),
      ...(hold === undefined ? {} : { hold_reason: hold })
    }
  };
}

export function parseCheckoutState(raw: unknown): ContractResult<CheckoutState> {
  const forbidden = carriesForbiddenKey(raw);
  if (forbidden !== null) return { ok: false, reason: forbidden };
  if (!isRecord(raw)) return { ok: false, reason: "checkout response is not an object" };
  const unexpected = exactKeys(raw, STATE_KEYS);
  if (unexpected !== null) return { ok: false, reason: unexpected };
  return parseCheckoutCommon(raw);
}

export function parseCheckoutStarted(raw: unknown): ContractResult<CheckoutStarted> {
  const forbidden = carriesForbiddenKey(raw);
  if (forbidden !== null) return { ok: false, reason: forbidden };
  if (!isRecord(raw)) return { ok: false, reason: "checkout response is not an object" };
  const unexpected = exactKeys(raw, [...STATE_KEYS, "tax_job_id", "checkout_token"]);
  if (unexpected !== null) return { ok: false, reason: unexpected };
  const common = parseCheckoutCommon(raw);
  if (!common.ok) return common;
  const taxJob = text(raw["tax_job_id"], MAX_ID);
  if (taxJob === null) return { ok: false, reason: "checkout did not report a tax job" };
  const token = optionalText(raw["checkout_token"], 200);
  if (!token.ok) return { ok: false, reason: "checkout token is out of bounds" };
  return {
    ok: true,
    value: {
      ...common.value,
      tax_job_id: taxJob,
      ...(token.value === undefined ? {} : { checkout_token: token.value })
    }
  };
}

export function parseInquiryReceipt(raw: unknown): ContractResult<InquiryReceipt> {
  const forbidden = carriesForbiddenKey(raw);
  if (forbidden !== null) return { ok: false, reason: forbidden };
  if (!isRecord(raw)) return { ok: false, reason: "inquiry response is not an object" };
  const unexpected = exactKeys(raw, ["inquiry_id", "status", "replayed"]);
  if (unexpected !== null) return { ok: false, reason: unexpected };
  const id = text(raw["inquiry_id"], MAX_ID);
  const status = text(raw["status"], 40);
  if (id === null || status === null) return { ok: false, reason: "inquiry response is incomplete" };
  const replayed = raw["replayed"];
  if (replayed !== undefined && typeof replayed !== "boolean") {
    return { ok: false, reason: "inquiry replay flag is not a boolean" };
  }
  return {
    ok: true,
    value: { inquiry_id: id, status, ...(replayed === undefined ? {} : { replayed }) }
  };
}

export function parseCustomerSession(raw: unknown): ContractResult<CustomerSessionReceipt> {
  const forbidden = carriesForbiddenKey(raw);
  if (forbidden !== null) return { ok: false, reason: forbidden };
  if (!isRecord(raw)) return { ok: false, reason: "session response is not an object" };
  const unexpected = exactKeys(raw, ["csrf_token", "expires_at"]);
  if (unexpected !== null) return { ok: false, reason: unexpected };
  const csrf = text(raw["csrf_token"], 200);
  const expires = text(raw["expires_at"], 60);
  if (csrf === null || expires === null) return { ok: false, reason: "session response is incomplete" };
  return { ok: true, value: { csrf_token: csrf, expires_at: expires } };
}

/** `POST /checkout/orders/{id}/continue` — a queued retrieval, never a result. */
export interface ContinuationReceipt {
  readonly status: "provider_retrieval_queued";
  readonly continuation_id: string;
}

export function parseContinuation(raw: unknown): ContractResult<ContinuationReceipt> {
  const forbidden = carriesForbiddenKey(raw);
  if (forbidden !== null) return { ok: false, reason: forbidden };
  if (!isRecord(raw)) return { ok: false, reason: "continuation response is not an object" };
  const unexpected = exactKeys(raw, ["status", "continuation_id"]);
  if (unexpected !== null) return { ok: false, reason: unexpected };
  if (raw["status"] !== "provider_retrieval_queued") {
    return { ok: false, reason: "continuation did not queue a provider retrieval" };
  }
  const id = text(raw["continuation_id"], MAX_ID);
  if (id === null) return { ok: false, reason: "continuation has no identifier" };
  return { ok: true, value: { status: "provider_retrieval_queued", continuation_id: id } };
}
